import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ReunionPluriprofessionnelle } from 'src/app/utils/interfaces/new/ReunionPluriprofessionnelle';
import { FiltresService } from '../filtres/filtres.service';
import { environment } from 'src/environments/environment';
import { CreationReunionPluriprofessionnelle } from 'src/app/utils/interfaces/new/CreationReunionPluriprofessionnelle';
import { ReunionPluriprofessionnellePatient } from 'src/app/utils/interfaces/new/ReunionPluriprofessionnellePatient';
import { ReunionPluriprofessionnellePersonnelSoignant } from 'src/app/utils/interfaces/new/ReunionPluriprofessionnellePersonnelSoignant';
import { Filtres } from 'src/app/utils/interfaces/new/filtres.interface';
import { Paginated } from 'src/app/utils/interfaces/new/paginated.interface';
import { SnackbarService } from '../snackbar/snackbar.service';

const MULTIPROFESSIONAL_MEETING_PATH = '/user/multiprofessional-meeting/';

@Injectable({
  providedIn: 'root'
})
export class MultiprofessionalMeetingService {

  private httpOptions = {
    withCredentials: true,
    observe: 'response' as 'response',
    headers: new HttpHeaders(),
    params: undefined
  };

  private basePath = environment.basePath + MULTIPROFESSIONAL_MEETING_PATH;

  constructor(
    private _http: HttpClient,
    private _filtresService: FiltresService,
    private _snack: SnackbarService
  ) { }

  public async getAllMultiprofessionnalMeetings(resourceId: string, filtre: Filtres = {
    perpage: -1,
    currentPage: 1,
  }): Promise<Paginated<ReunionPluriprofessionnelle>> {
    return new Promise<Paginated<ReunionPluriprofessionnelle>>(async (resolve, reject) => {
      try {
        this.httpOptions.headers = this._filtresService.setHeaders(filtre);
        const res = await this._http.get(`${this.basePath}resource/${resourceId}`, this.httpOptions).toPromise();
        const meetings = res.body as Array<ReunionPluriprofessionnelle>;
        const paginated: Paginated<ReunionPluriprofessionnelle> = {
          page: Number(res.headers.get('x-page')),
          perpage: Number(res.headers.get('x-per-page')),
          total: Number(res.headers.get('x-total')),
          objects: meetings
        };
        resolve(paginated);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async getOneMultiprofessionnalMeeting(meetingId: string): Promise<ReunionPluriprofessionnelle> {
    return new Promise<ReunionPluriprofessionnelle>(async (resolve, reject) => {
      try {
        const res = await this._http.get(`${this.basePath}${meetingId}`, this.httpOptions).toPromise();
        resolve(res.body as ReunionPluriprofessionnelle);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async createMultiprofessionnalMeetings(meeting: CreationReunionPluriprofessionnelle): Promise<ReunionPluriprofessionnelle> {
    return new Promise<ReunionPluriprofessionnelle>(async (resolve, reject) => {
      try {
        const updatedMeeting = await this._http.post<ReunionPluriprofessionnelle>(
          `${this.basePath}`,
          meeting,
          this.httpOptions
        ).toPromise();
        resolve(updatedMeeting.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async updateMultiprofessionnalMeetings(
    meetingId: string,
    meeting: CreationReunionPluriprofessionnelle
  ): Promise<ReunionPluriprofessionnelle> {
    return new Promise<ReunionPluriprofessionnelle>(async (resolve, reject) => {
      try {
        const updatedMeeting = await this._http.put<ReunionPluriprofessionnelle>(
          `${this.basePath}${meetingId}`,
          meeting,
          this.httpOptions
        ).toPromise();
        resolve(updatedMeeting.body);
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public async deleteMultiprofessionnalMeetings(meetingId: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        await this._http.delete(`${this.basePath}${meetingId}`, this.httpOptions).toPromise();
        resolve();
      } catch (e) {
        this._snack.openSnackBar(e.error.message);
        reject(e);
      }
    });
  }

  public convertMeetingToCreationMeeting(meeting: ReunionPluriprofessionnelle): CreationReunionPluriprofessionnelle {
    const patients: Array<string> = [];
    if (meeting.patients) {
      for (const p of meeting.patients) {
        patients.push(p.id);
      }
    }

    const caregivers: Array<ReunionPluriprofessionnellePersonnelSoignant> = [];
    if (meeting.personnelSoignant) {
      for (const p of meeting.personnelSoignant) {
        caregivers.push({
          id: p.id && p.id !== '' ? p.id : undefined,
          type: p.type,
          nom: p.nom,
          estPresent: p.estPresent
        });
      }
    }

    return {
      coordinateurResourceId: meeting.coordinateur.id,
      secretaireResourceId: meeting.secretaire.id,
      adresse: meeting.adresse ? meeting.adresse : undefined,
      compteRendu: meeting.compteRendu ? meeting.compteRendu : undefined,
      createur: meeting.createur ? meeting.createur.id : undefined,
      dateDebut: meeting.dateDebut ? meeting.dateDebut : undefined,
      dateFin: meeting.dateFin ? meeting.dateFin : undefined,
      estReunionDInformation: meeting.estReunionDInformation,
      estReunionDeConcertation: meeting.estReunionDeConcertation,
      estValide: meeting.estValide,
      impressionEnTeteCoordinateur: meeting.impressionEnTeteCoordinateur,
      impressionInformation: meeting.impressionInformation,
      ordreDuJour: meeting.ordreDuJour ? meeting.ordreDuJour : undefined,
      titre: meeting.titre ? meeting.titre : undefined,
      salleRessourceId: meeting.salle ? meeting.salle.id : undefined,
      patients: meeting.patients ? patients : undefined,
      personnelSoignant: meeting.personnelSoignant ? caregivers : undefined,
      estTermine: meeting.estTermine
    } as CreationReunionPluriprofessionnelle;
  }
}
